import * as THREE from "three";

// Generate a facade texture (day albedo + night emissive) using canvas.
// style: 'twin' (dense narrow vertical aluminum ribs), 'glass' (blue curtain wall),
// 'stone' (old office building), 'modern' (dark glass tower).
export function makeFacade(style = "glass", opts = {}) {
  const W = 256;
  const H = 512;
  const day = document.createElement("canvas");
  day.width = W;
  day.height = H;
  const emis = document.createElement("canvas");
  emis.width = W;
  emis.height = H;
  const c = day.getContext("2d");
  const e = emis.getContext("2d");
  e.fillStyle = "#000000";
  e.fillRect(0, 0, W, H);

  if (style === "twin") {
    // Silver aluminum with very dense narrow vertical windows (Yamasaki facade)
    c.fillStyle = "#c9ccce";
    c.fillRect(0, 0, W, H);
    const cols = 42;
    const cw = W / cols;
    for (let i = 0; i < cols; i++) {
      const x = i * cw;
      // aluminum mullion
      c.fillStyle = "#d7dadb";
      c.fillRect(x, 0, cw * 0.55, H);
      // recessed dark window slit
      c.fillStyle = "#2b3138";
      c.fillRect(x + cw * 0.55, 0, cw * 0.45, H);
    }
    // faint horizontal floor lines
    c.fillStyle = "rgba(120,125,130,0.25)";
    for (let y = 0; y < H; y += 10) c.fillRect(0, y, W, 1);
    // night: warm lit slits, random
    for (let i = 0; i < cols; i++) {
      const x = i * cw;
      for (let y = 0; y < H; y += 10) {
        if (Math.random() > 0.78) {
          e.fillStyle = Math.random() > 0.5 ? "#ffd98a" : "#ffe9c2";
          e.fillRect(x + cw * 0.55, y + 1, cw * 0.45, 7);
        }
      }
    }
  } else if (style === "glass") {
    // 1 WTC style reflective blue curtain wall
    const g = c.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, "#bfd6e6");
    g.addColorStop(0.5, "#8fb2c9");
    g.addColorStop(1, "#6f97b3");
    c.fillStyle = g;
    c.fillRect(0, 0, W, H);
    const cols = 16;
    const rows = 40;
    const cw = W / cols;
    const rh = H / rows;
    for (let i = 0; i < cols; i++)
      for (let j = 0; j < rows; j++) {
        const shade = 0.75 + Math.random() * 0.25;
        c.fillStyle = `rgba(${Math.floor(150 * shade)},${Math.floor(
          185 * shade
        )},${Math.floor(210 * shade)},1)`;
        c.fillRect(i * cw + 1, j * rh + 1, cw - 2, rh - 2);
      }
    for (let i = 0; i < cols; i++)
      for (let j = 0; j < rows; j++) {
        if (Math.random() > 0.85) {
          e.fillStyle = "#cfe6ff";
          e.fillRect(i * cw + 1, j * rh + 1, cw - 2, rh - 2);
        }
      }
  } else if (style === "stone") {
    c.fillStyle = "#c7b8a3";
    c.fillRect(0, 0, W, H);
    const cols = 12;
    const rows = 26;
    const cw = W / cols;
    const rh = H / rows;
    for (let i = 0; i < cols; i++)
      for (let j = 0; j < rows; j++) {
        c.fillStyle = "#5b6470";
        c.fillRect(i * cw + 3, j * rh + 3, cw - 6, rh - 7);
        if (Math.random() > 0.7) {
          e.fillStyle = "#ffdf9e";
          e.fillRect(i * cw + 3, j * rh + 3, cw - 6, rh - 7);
        }
      }
  } else {
    // modern glass (mid blue-grey so it never reads pure black when backlit)
    c.fillStyle = "#55637a";
    c.fillRect(0, 0, W, H);
    const cols = 14;
    const rows = 34;
    const cw = W / cols;
    const rh = H / rows;
    for (let i = 0; i < cols; i++)
      for (let j = 0; j < rows; j++) {
        const s = 0.72 + Math.random() * 0.4;
        c.fillStyle = `rgba(${Math.floor(108 * s)},${Math.floor(126 * s)},${Math.floor(
          148 * s
        )},1)`;
        c.fillRect(i * cw + 1, j * rh + 1, cw - 2, rh - 2);
        if (Math.random() > 0.86) {
          e.fillStyle = "#ffe6b0";
          e.fillRect(i * cw + 1, j * rh + 1, cw - 2, rh - 2);
        }
      }
  }

  const map = new THREE.CanvasTexture(day);
  const emissiveMap = new THREE.CanvasTexture(emis);
  [map, emissiveMap].forEach((t) => {
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.anisotropy = 4;
    t.colorSpace = THREE.SRGBColorSpace;
  });
  return { map, emissiveMap };
}

// 1 WTC antiprism geometry: square base -> square (rotated 45) top, 8 triangular facets.
export function antiprismGeometry(rBottom, rTop, height) {
  const g = new THREE.BufferGeometry();
  const B = [],
    T = [];
  for (let i = 0; i < 4; i++) {
    const ab = (Math.PI / 180) * (i * 90 + 45);
    const at = (Math.PI / 180) * (i * 90);
    B.push([Math.cos(ab) * rBottom, 0, Math.sin(ab) * rBottom]);
    T.push([Math.cos(at) * rTop, height, Math.sin(at) * rTop]);
  }
  const pos = [];
  const uv = [];
  const pushTri = (a, b, cc) => {
    pos.push(...a, ...b, ...cc);
    uv.push(0, 0, 1, 0, 0.5, 1);
  };
  for (let i = 0; i < 4; i++) {
    const ni = (i + 1) % 4;
    pushTri(B[i], T[ni], T[i]);
    pushTri(B[i], B[ni], T[ni]);
  }
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  g.computeVertexNormals();
  return g;
}


// Austin J. Tobin Plaza concentric paving pattern (radial + rings).
export function makePaving() {
  const S = 1024;
  const cv = document.createElement("canvas");
  cv.width = cv.height = S;
  const c = cv.getContext("2d");
  c.fillStyle = "#b7ab97";
  c.fillRect(0, 0, S, S);
  const cx = S / 2;
  const cy = S / 2;
  // subtle granite speckle
  for (let i = 0; i < 5000; i++) {
    const g = 150 + Math.random() * 60;
    c.fillStyle = `rgba(${g},${g - 8},${g - 22},0.15)`;
    c.fillRect(Math.random() * S, Math.random() * S, 2, 2);
  }
  // concentric rings
  for (let r = 30; r < S / 2; r += 34) {
    c.beginPath();
    c.arc(cx, cy, r, 0, Math.PI * 2);
    c.lineWidth = 3;
    c.strokeStyle = "rgba(90,84,72,0.55)";
    c.stroke();
  }
  // radial spokes
  const spokes = 48;
  for (let i = 0; i < spokes; i++) {
    const a = (i / spokes) * Math.PI * 2;
    c.beginPath();
    c.moveTo(cx, cy);
    c.lineTo(cx + Math.cos(a) * (S / 2), cy + Math.sin(a) * (S / 2));
    c.lineWidth = 1.2;
    c.strokeStyle = "rgba(90,84,72,0.28)";
    c.stroke();
  }
  // inner fountain ring emphasis
  c.beginPath();
  c.arc(cx, cy, 150, 0, Math.PI * 2);
  c.lineWidth = 8;
  c.strokeStyle = "rgba(70,64,52,0.7)";
  c.stroke();
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

// Carved masonry stone texture for the Brooklyn Bridge arches/towers.
export function makeStone() {
  const S = 256;
  const cv = document.createElement("canvas");
  cv.width = cv.height = S;
  const c = cv.getContext("2d");
  c.fillStyle = "#c3ad8c";
  c.fillRect(0, 0, S, S);
  const bh = 26;
  const bw = 42;
  for (let y = 0, row = 0; y < S; y += bh, row++) {
    const off = row % 2 ? bw / 2 : 0;
    for (let x = -bw; x < S; x += bw) {
      const g = 150 + Math.random() * 40;
      c.fillStyle = `rgb(${g + 20},${g + 6},${g - 20})`;
      c.fillRect(x + off + 1.5, y + 1.5, bw - 3, bh - 3);
      c.strokeStyle = "rgba(70,58,40,0.6)";
      c.lineWidth = 2;
      c.strokeRect(x + off + 1.5, y + 1.5, bw - 3, bh - 3);
    }
  }
  const tex = new THREE.CanvasTexture(cv);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(2, 6);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

// Engraved bronze parapet name-band (evokes the memorial's inscribed names).
export function makeNames() {
  const W = 1024, H = 96;
  const cv = document.createElement("canvas");
  cv.width = W; cv.height = H;
  const c = cv.getContext("2d");
  c.fillStyle = "#4a3a1e"; c.fillRect(0, 0, W, H);
  const NAMES = ["ANDREW","MARIA","JOHN","FATIMA","DAVID","SUNG","LUIS","AMY","RAJ","GRACE","OMAR","NINA","PAUL","LEE","EVA","KWAME","SARAH","TOM","ILYA","MEI"];
  c.font = "600 15px Georgia, serif";
  c.textBaseline = "middle";
  for (let row = 0; row < 3; row++) {
    let x = 8 + row * 40;
    const y = 20 + row * 30;
    while (x < W) {
      const n = NAMES[Math.floor(Math.random() * NAMES.length)] + " " + NAMES[Math.floor(Math.random() * NAMES.length)][0] + ".";
      c.fillStyle = "#caa martial".replace(" martial", "34");
      c.fillText(n, x, y);
      x += c.measureText(n).width + 26;
    }
  }
  const tex = new THREE.CanvasTexture(cv);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(6, 1);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}


// Mottled green foliage/leaf texture for tree canopies.
export function makeCanopy() {
  const S = 128;
  const cv = document.createElement("canvas");
  cv.width = cv.height = S;
  const c = cv.getContext("2d");
  c.fillStyle = "#3f6b2e";
  c.fillRect(0, 0, S, S);
  for (let i = 0; i < 2800; i++) {
    const g = 70 + Math.random() * 95;
    const r = 22 + Math.random() * 46;
    const b = 26 + Math.random() * 30;
    c.fillStyle = `rgba(${r},${g},${b},${0.45 + Math.random() * 0.45})`;
    const rad = 1 + Math.random() * 3.2;
    c.beginPath();
    c.arc(Math.random() * S, Math.random() * S, rad, 0, Math.PI * 2);
    c.fill();
  }
  // a few darker gaps for depth
  for (let i = 0; i < 220; i++) {
    c.fillStyle = `rgba(18,38,16,${0.2 + Math.random() * 0.3})`;
    c.beginPath();
    c.arc(Math.random() * S, Math.random() * S, 1 + Math.random() * 2, 0, Math.PI * 2);
    c.fill();
  }
  const tex = new THREE.CanvasTexture(cv);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(2, 2);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

// Vertical sheeting water texture for the memorial pool waterfalls (scroll .offset.y).
export function makeWaterfall() {
  const W = 64, H = 256;
  const cv = document.createElement("canvas");
  cv.width = W;
  cv.height = H;
  const c = cv.getContext("2d");
  const g = c.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, "#e6f3f6");
  g.addColorStop(0.12, "#b6d6de");
  g.addColorStop(0.6, "#4f8794");
  g.addColorStop(1, "#22525d");
  c.fillStyle = g;
  c.fillRect(0, 0, W, H);
  for (let i = 0; i < 90; i++) {
    const x = Math.random() * W;
    c.fillStyle = `rgba(255,255,255,${0.12 + Math.random() * 0.4})`;
    c.fillRect(x, 0, 0.5 + Math.random() * 1.6, H);
  }
  const tex = new THREE.CanvasTexture(cv);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(5, 1.5);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

// Dark asphalt texture for the street network.
export function makeAsphalt() {
  const S = 256;
  const cv = document.createElement("canvas");
  cv.width = cv.height = S;
  const c = cv.getContext("2d");
  c.fillStyle = "#33363b";
  c.fillRect(0, 0, S, S);
  for (let i = 0; i < 9000; i++) {
    const v = 28 + Math.random() * 44;
    c.fillStyle = `rgba(${v},${v},${v + 5},${0.12 + Math.random() * 0.25})`;
    c.fillRect(Math.random() * S, Math.random() * S, 1, 1);
  }
  const tex = new THREE.CanvasTexture(cv);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(3, 3);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

